import React from 'react';

import { Button, Group, LoadingOverlay, Modal, Stack, Text, Title } from '@mantine/core';
import useInvoice from 'hooks/invoice/useInvoice';
import useSendInvoice from 'hooks/invoice/useSendInvoice';
import notificationHandler from 'utils/notifications';

import InvoiceSummaryCard from './InvoiceSummaryCard';

interface InvoiceSendModalProps {
  invoiceId: string;
  opened: boolean;
  close: () => void;
}

const InvoiceSendModal = ({ invoiceId, opened, close }: InvoiceSendModalProps): JSX.Element => {
  const sendInvoiceMutation = useSendInvoice();

  const { errorNotification, successNotification } = notificationHandler();

  const { data, isLoading, isSuccess } = useInvoice(invoiceId);

  const handleSendInvoice = (): void => {
    sendInvoiceMutation.mutate(
      { invoiceId: invoiceId || '', athleteId: data?.data.athlete.athleteId || '' },
      {
        onError: error => {
          errorNotification(
            'Failed to send invoice. Please try again or contact support.',
            error.response?.data.message,
          );
          close();
        },
        onSuccess: () => {
          successNotification(
            `Invoice sent to athlete's email.`,
            `Invoice with details has been sent to athlete's email.`,
          );
          close();
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<Title order={4}>Email Receipt</Title>}
      trapFocus
      centered
      size='lg'
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <Stack px='md' w='100%' align='center'>
        {isSuccess && data?.data && <InvoiceSummaryCard invoice={data.data} />}
        <Text fw={500} py='sm'>
          Are you sure you want to email this receipt to the athlete?
        </Text>
      </Stack>
      <Group position='right' pt='md'>
        <Button onClick={handleSendInvoice} disabled={isLoading} loading={sendInvoiceMutation.isLoading}>
          Send
        </Button>
      </Group>
    </Modal>
  );
};

export default InvoiceSendModal;
